import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { DataTableComponent, TableColumn, PaginationEvent } from '@shared/components/data-table/data-table.component';
import { SearchFormComponent, SearchFormData } from '@shared/components/search-form/search-form.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { OrderAuditModel } from './order-audit.model';
import { OrderAuditService } from './order-audit.service';
import { StatusFilterComponent, ActionButton } from "@shared/components/status-filter/status-filter.component";

@Component({
  selector: 'app-order-audit',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DataTableComponent,
    SearchFormComponent,
    StatusFilterComponent,
    TranslateModule
  ],
  templateUrl: './order-audit.component.html',
  styleUrl: './order-audit.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderAuditComponent implements OnInit {
  private fb = inject(FormBuilder);
  private orderAuditService = inject(OrderAuditService);
  private translateService = inject(TranslateService);

  searchForm!: FormGroup;
  orders: OrderAuditModel[] = [];
  selectedOrders: OrderAuditModel[] = [];
  loading = false;
  totalRecords = 0;
  first = 0;
  rows = 20;

  // Tab options
  auditTabs = [
    { label: 'order.audit.tabs.pre_sourcing_pending_review', value: 'pre-sourcing' },
    { label: 'order.audit.tabs.pre_shipment_pending_review', value: 'pre-shipment' }
  ];
  activeTab = 'pre-sourcing';

  // Status filter options
  statusOptions = [
    { key: 'all', label: 'order.audit.status.all', active: true },
    { key: 'first-review', label: 'order.audit.status.first_review_pending', },
    { key: 'second-review', label: 'order.audit.status.second_review_pending', },
    { key: 'rejected', label: 'order.audit.status.review_rejected', }
  ];
  activeStatus = 'all';

  // Button configurations
  primaryButton: ActionButton = {
    label: 'order.audit.actions.approve',
    severity: 'info',
    outlined: false,
    disabled: false,
    show: true
  };

  secondaryButton: ActionButton = {
    label: 'order.audit.actions.reject',
    severity: 'info',
    outlined: true,
    disabled: false,
    show: true
  };

  // Dropdown options - mapping of values to translation keys
  private orderSourceMap = {
    'Facebook': 'order.audit.options.facebook',
    'Shopee': 'order.audit.options.shopee',
    'Tmall': 'order.audit.options.tmall'
  };
  private salesChannelMap = {
    'Online': 'order.audit.options.online',
    'Offline': 'order.audit.options.offline',
    'Wholesale': 'order.audit.options.wholesale'
  };
  private salesStoreMap = {
    'Store A': 'order.audit.options.store_a',
    'Store B': 'order.audit.options.store_b',
    'Store C': 'order.audit.options.store_c'
  };
  private buyerNotesMap = {
    'Contains': 'order.audit.options.contains',
    'Not Contains': 'order.audit.options.not_contains',
    'Equals': 'order.audit.options.equals',
    'Not Equals': 'order.audit.options.not_equals'
  };
  private merchantNotesMap = {
    'Contains': 'order.audit.options.contains',
    'Not Contains': 'order.audit.options.not_contains',
    'Equals': 'order.audit.options.equals',
    'Not Equals': 'order.audit.options.not_equals'
  };
  private giftMap = {
    'Yes': 'order.audit.options.yes',
    'No': 'order.audit.options.no'
  };

  orderSources = ['Facebook', 'Shopee', 'Tmall'];
  salesChannels = ['Online', 'Offline', 'Wholesale'];
  salesStores = ['Store A', 'Store B', 'Store C'];
  buyerNotesOptions = ['Contains', 'Not Contains', 'Equals', 'Not Equals'];
  merchantNotesOptions = ['Contains', 'Not Contains', 'Equals', 'Not Equals'];
  giftOptions = ['Yes', 'No'];

  // Table columns
  columns: TableColumn[] = [
    { label: 'order.audit.table.third_party_order_number', prop: 'thirdPartyOrderNumber', width: 150 },
    { label: 'order.audit.table.internal_order_number', prop: 'internalOrderNumber', width: 150 },
    { label: 'order.audit.table.tags', prop: 'tags', width: 100 },
    { label: 'order.audit.table.order_source', prop: 'orderSource', width: 120 },
    { label: 'order.audit.table.audit_status', prop: 'auditStatus', width: 120 },
    { label: 'order.audit.table.product_line_count', prop: 'productLineCount', width: 120 },
    { label: 'order.audit.table.sales_store', prop: 'salesStore', width: 120 },
    { label: 'order.audit.table.buyer_nickname', prop: 'buyerNickname', width: 120 },
    { label: 'order.audit.table.sales_channel', prop: 'salesChannel', width: 120 },
    { label: 'order.audit.table.buyer_notes', prop: 'buyerNotes', width: 150 },
    { label: 'order.audit.table.merchant_notes', prop: 'merchantNotes', width: 150 },
    { label: 'order.audit.table.recipient_name', prop: 'recipientName', width: 120 },
    { label: 'order.audit.table.recipient_phone', prop: 'recipientPhone', width: 120 },
    { label: 'order.audit.table.recipient_address', prop: 'recipientAddress', width: 200 },
    { label: 'order.audit.table.shipment_status', prop: 'shipmentStatus', width: 120 },
    { label: 'order.audit.table.receipt_status', prop: 'receiptStatus', width: 120 },
    { label: 'order.audit.table.total_product_amount', prop: 'totalProductAmount', width: 120 },
    { label: 'order.audit.table.buyer_payable', prop: 'buyerPayable', width: 120 },
    { label: 'order.audit.table.total_product_cost', prop: 'totalProductCost', width: 120 },
    { label: 'order.audit.table.third_party_order_time', prop: 'thirdPartyOrderTime', width: 150 },
    { label: 'order.audit.table.payment_time', prop: 'paymentTime', width: 150 },
    { label: 'order.audit.table.internal_create_time', prop: 'internalCreateTime', width: 150 },
    { label: 'order.audit.table.expected_shipment_time', prop: 'expectedShipmentTime', width: 150 },
    { label: 'order.audit.table.sourcing_status', prop: 'sourcingStatus', width: 120 },
    { label: 'order.audit.table.suspension_status', prop: 'suspensionStatus', width: 120 },
    { label: 'order.audit.table.preemption_writeback_status', prop: 'preemptionWritebackStatus', width: 150 },
    { label: 'order.audit.table.shipment_writeback_status', prop: 'shipmentWritebackStatus', width: 150 },
    { label: 'order.audit.table.has_gifts', prop: 'hasGifts', width: 100 }
  ];

  ngOnInit(): void {
    this.initForm();
    this.loadOrders();
  }

  private initForm(): void {
    this.searchForm = this.fb.group({
      orderNumber: [''],
      productName: [''],
      skuCode: [''],
      orderSource: [''],
      salesChannel: [''],
      salesStore: [{ value: '', disabled: true }],
      buyerNickname: [''],
      recipientName: [''],
      recipientPhone: [''],
      buyerNotes: [''],
      buyerNotesValue: [''],
      merchantNotes: [''],
      merchantNotesValue: [''],
      hasGifts: [''],
      thirdPartyOrderTimeStart: [''],
      thirdPartyOrderTimeEnd: [''],
      paymentTimeStart: [''],
      paymentTimeEnd: [''],
      internalCreateTimeStart: [''],
      internalCreateTimeEnd: ['']
    });
  }

  loadOrders(): void {
    this.loading = true;
    const searchParams = this.searchForm.value;
    searchParams.auditType = this.activeTab;
    searchParams.status = this.activeStatus;
    searchParams.first = this.first;
    searchParams.rows = this.rows;

    this.orderAuditService.getOrders(searchParams).subscribe({
      next: (response) => {
        this.orders = response.data;
        this.totalRecords = response.total;
        this.loading = false;
      },
      error: (error) => {
        console.error('Failed to load orders:', error);
        this.loading = false;
      }
    });
  }

  onSearch(searchData: SearchFormData): void {
    this.first = 0;
    this.loadOrders();
  }

  onReset(): void {
    this.searchForm.reset();
    this.activeStatus = 'all';
    this.first = 0;
    this.loadOrders();
  }

  onTabChange(event: any): void {
    this.activeTab = event.index === 0 ? 'pre-sourcing' : 'pre-shipment';
    this.first = 0;
    this.loadOrders();
  }

  onStatusChange(status: string): void {
    this.activeStatus = status;
    this.first = 0;
    this.loadOrders();
  }

  onPageChange(event: PaginationEvent): void {
    this.first = event.first || 0;
    this.rows = event.rows || 20;
    this.loadOrders();
  }

  onSelectionChange(event: any): void {
    this.selectedOrders = event;
  }

  approveOrders(): void {
    if (this.selectedOrders.length === 0) {
      console.warn('Please select orders to approve');
      return;
    }

    const orderIds = this.selectedOrders.map(order => order.id);
    // this.orderAuditService.approveOrders(orderIds).subscribe({
    //   next: () => {
    //     console.log('Orders approved successfully');
    //     this.selectedOrders = [];
    //     this.loadOrders();
    //   },
    //   error: (error) => {
    //     console.error('Failed to approve orders:', error);
    //   }
    // });
  }

  rejectOrders(): void {
    if (this.selectedOrders.length === 0) {
      console.warn('Please select orders to reject');
      return;
    }

    const orderIds = this.selectedOrders.map(order => order.id);
    // this.orderAuditService.rejectOrders(orderIds).subscribe({
    //   next: () => {
    //     console.log('Orders rejected successfully');
    //     this.selectedOrders = [];
    //     this.loadOrders();
    //   },
    //   error: (error) => {
    //     console.error('Failed to reject orders:', error);
    //   }
    // });
  }

  exportData(): void {
    // this.orderAuditService.exportOrders(this.searchForm.value).subscribe({
    //   next: (response) => {
    //     // Handle file download
    //     const blob = new Blob([response], { type: 'text/csv' });
    //     const url = window.URL.createObjectURL(blob);
    //     const link = document.createElement('a');
    //     link.href = url;
    //     link.download = 'order-audit-data.csv';
    //     link.click();
    //     window.URL.revokeObjectURL(url);
    //   },
    //   error: (error) => {
    //     console.error('Failed to export data:', error);
    //   }
    // });
  }

  refreshData(): void {
    this.loadOrders();
  }

  getStatusSeverity(status: string): string {
    switch (status) {
      case 'First Review Pending':
        return 'warning';
      case 'Second Review Pending':
        return 'info';
      case 'Review Rejected':
        return 'danger';
      default:
        return 'success';
    }
  }

  // Dropdown option getters
  getOrderSourceOptions() {
    return this.orderSources.map(s => ({
      label: this.translateService.instant(this.orderSourceMap[s as keyof typeof this.orderSourceMap]),
      value: s
    }));
  }

  getSalesChannelOptions() {
    return this.salesChannels.map(s => ({
      label: this.translateService.instant(this.salesChannelMap[s as keyof typeof this.salesChannelMap]),
      value: s
    }));
  }

  getSalesStoreOptions() {
    return this.salesStores.map(s => ({
      label: this.translateService.instant(this.salesStoreMap[s as keyof typeof this.salesStoreMap]),
      value: s
    }));
  }

  getBuyerNotesOptions() {
    return this.buyerNotesOptions.map(s => ({
      label: this.translateService.instant(this.buyerNotesMap[s as keyof typeof this.buyerNotesMap]),
      value: s
    }));
  }

  getMerchantNotesOptions() {
    return this.merchantNotesOptions.map(s => ({
      label: this.translateService.instant(this.merchantNotesMap[s as keyof typeof this.merchantNotesMap]),
      value: s
    }));
  }

  getGiftOptions() {
    return this.giftOptions.map(s => ({
      label: this.translateService.instant(this.giftMap[s as keyof typeof this.giftMap]),
      value: s
    }));
  }
} 